import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Heart, Plus, Check, FolderHeart } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { toast } from 'sonner';

const FavoriteButton = ({ listingId, size = 'sm', className = '' }) => {
  const [lists, setLists] = useState([]);
  const [isFavorited, setIsFavorited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/favorites/check/${listingId}`);
      const favLists = res.data.lists || [];
      setLists(favLists);
      setIsFavorited(favLists.some(l => l.contains));
    } catch (e) {
      // Deleteently handle 401 (not logged in) — no console noise for anonymous users
      if (e.response?.status !== 401) {
        console.error(e);
      }
    }
  }, [listingId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleQuickToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/favorites/toggle`, { listing_id: listingId });
      setIsFavorited(res.data.is_favorite);
      toast.success(res.data.is_favorite ? 'Favorilere added' : 'Removed from favourites');
      fetchStatus();
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Log in to add to favourites');
      } else {
        toast.error('An error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleList = async (listId, contains) => {
    try {
      if (contains) {
        await axios.delete(`${API}/favorites/lists/${listId}/remove/${listingId}`);
      } else {
        await axios.post(`${API}/favorites/lists/${listId}/add`, { listing_id: listingId });
      }
      fetchStatus();
    } catch (e) { console.error(e);
      toast.error('An error occurred');
    }
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/favorites/lists`, { name: newListName.trim() });
      await axios.post(`${API}/favorites/lists/${res.data.list_id}/add`, { listing_id: listingId });
      setNewListName('');
      toast.success(`"${res.data.name}" listesi created`);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'List could not be created');
    } finally {
      setCreating(false);
    }
  };

  const iconSize = size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
  const btnSize = size === 'lg' ? 'h-10 w-10' : 'h-8 w-8';

  return (
    <div className={`flex items-center gap-0.5 ${className}`} onClick={(e) => e.stopPropagation()}>
      {/* Quick toggle (heart) */}
      <button
        onClick={handleQuickToggle}
        disabled={loading}
        className={`${btnSize} rounded-full flex items-center justify-center transition-all ${
          isFavorited
            ? 'bg-red-50 text-red-500 hover:bg-red-100'
            : 'bg-white/80 text-slate-400 hover:text-red-400 hover:bg-red-50'
        } backdrop-blur-sm shadow-sm border border-slate-200/50`}
        title={isFavorited ? 'Remove from favourites' : 'Add to favourites'}
        data-testid={`fav-toggle-${listingId}`}
      >
        <Heart className={`${iconSize} ${isFavorited ? 'fill-current' : ''}`} />
      </button>

      {/* List selector (popover) */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            onClick={(e) => { e.stopPropagation(); }}
            onPointerDown={(e) => { e.stopPropagation(); }}
            className={`${size === 'lg' ? 'h-10 w-10' : 'h-8 w-8'} rounded-full flex items-center justify-center bg-white/80 text-slate-400 hover:text-slate-600 hover:bg-slate-100 backdrop-blur-sm shadow-sm border border-slate-200/50 transition-all`}
            title="Add to list"
            data-testid={`fav-list-btn-${listingId}`}
          >
            <FolderHeart className={size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="end" side="bottom" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          <div className="px-3 py-2.5 border-b bg-slate-50">
            <p className="text-sm font-semibold text-slate-700">Listelerim</p>
          </div>
          <div className="max-h-48 overflow-y-auto py-1">
            {lists.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-4">No lists yet</p>
            )}
            {lists.map(l => (
              <button
                key={l.list_id}
                onClick={() => handleToggleList(l.list_id, l.contains)}
                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 transition-colors text-left"
                data-testid={`fav-list-item-${l.list_id}`}
              >
                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                  l.contains ? 'bg-red-500 text-white' : 'border-2 border-slate-300'
                }`}>
                  {l.contains && <Check className="w-3 h-3" />}
                </div>
                <span className="text-sm text-slate-700 truncate">{l.name}</span>
              </button>
            ))}
          </div>
          <div className="border-t px-3 py-2.5">
            <div className="flex items-center gap-1.5">
              <Input
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="New list name..."
                className="h-8 text-xs"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateList()}
                data-testid="fav-new-list-input"
              />
              <Button
                size="sm"
                className="h-8 px-2 bg-emerald-900"
                onClick={handleCreateList}
                disabled={creating || !newListName.trim()}
                data-testid="fav-create-list-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default FavoriteButton;
